import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { useCreateSetupIntent } from '@/lib/query/useCreateSetupIntent';
import { useUpdatePaymentMethod } from '@/lib/query/useUpdatePaymentMethod';

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
);

interface UpdatePaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function PaymentForm({ onClose }: { onClose: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const updatePaymentMethod = useUpdatePaymentMethod();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'An error occurred');
        setIsProcessing(false);
        return;
      }

      if (setupIntent?.payment_method) {
        await updatePaymentMethod.mutateAsync(
          setupIntent.payment_method as string
        );
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && <div className="text-destructive text-sm">{error}</div>}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || isProcessing}>
          {isProcessing ? 'Processing...' : 'Update Payment Method'}
        </Button>
      </div>
    </form>
  );
}

export function UpdatePaymentMethodModal({
  isOpen,
  onClose,
}: UpdatePaymentMethodModalProps) {
  const { resolvedTheme } = useTheme();
  const createSetupIntent = useCreateSetupIntent();
  const { mutate: mutateSetupIntent } = createSetupIntent;
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [hasAttempted, setHasAttempted] = useState(false);

  // Create setup intent when modal opens (only once)
  useEffect(() => {
    if (isOpen && !clientSecret && !hasAttempted) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setHasAttempted(true);
        mutateSetupIntent(undefined, {
          onSuccess: (data) => {
            setClientSecret(data.clientSecret);
          },
        });
      }, 0);
    }
  }, [isOpen, clientSecret, hasAttempted, mutateSetupIntent]);

  const appearance = {
    theme: (resolvedTheme === 'dark' ? 'night' : 'stripe') as
      | 'night'
      | 'stripe',
    variables: {
      colorPrimary: '#9b5eee',
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Update Payment Method">
      {createSetupIntent.isPending && (
        <div className="text-muted-foreground py-8 text-center">Loading...</div>
      )}
      {createSetupIntent.isError && (
        <div className="text-destructive py-4">
          Failed to initialize payment form. Please try again.
        </div>
      )}
      {clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
          <PaymentForm onClose={onClose} />
        </Elements>
      )}
    </BaseModal>
  );
}
